// Tipos compartilhados pelos fluxos de onboarding (workspace + org).
import type { IconName } from "@/components/common/Icon";
import type { OnbStepDef } from "@/components/onboarding/OnbShell";

// ---------------------------------------------------------------- Workspace --

export type WsForm = {
  docType: "cnpj" | "cpf";
  nomeWorkspace: string;
  cnpj: string;
  cpf: string;
  razao: string;
  fantasia: string;
  nomePf: string;
  endereco: string; // logradouro
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  cidade: string;
  uf: string;
  telefone: string;
  emailEmpresa: string;
  respNome: string;
  respCpf: string;
  respCargo: string;
  ownerNome: string;
  ownerEmail: string;
  ownerTelefone: string;
  ownerSenha: string;
  ownerSenha2: string;
  emailVerificado: boolean;
  telefoneVerificado: boolean;
  aceiteTermos: boolean;
  aceiteLgpd: boolean;
  fuso: string;
  notifEmail: boolean;
  notifWhatsapp: boolean;
  corMarca: string;
  convites: Array<{ nome: string; email: string; papel: string }>;
};

export type WsStepId = "workspace" | "config" | "equipe";
export type WsStepDef = OnbStepDef & { id: WsStepId };

export const WS_STEPS: WsStepDef[] = [
  {
    id: "workspace",
    label: "Workspace",
    short: "Dados do workspace",
    required: true,
    icon: "building",
  },
  {
    id: "config",
    label: "Configurar workspace",
    short: "Marca, fuso, notificações",
    required: false,
    icon: "settings",
  },
  {
    id: "equipe",
    label: "Equipe",
    short: "Convidar membros",
    required: false,
    icon: "users",
  },
];

export const WS_INITIAL_FORM: WsForm = {
  docType: "cnpj",
  nomeWorkspace: "",
  cnpj: "",
  cpf: "",
  razao: "",
  fantasia: "",
  nomePf: "",
  endereco: "",
  numero: "",
  complemento: "",
  bairro: "",
  cep: "",
  cidade: "",
  uf: "",
  telefone: "",
  emailEmpresa: "",
  respNome: "",
  respCpf: "",
  respCargo: "",
  ownerNome: "",
  ownerEmail: "",
  ownerTelefone: "",
  ownerSenha: "",
  ownerSenha2: "",
  emailVerificado: false,
  telefoneVerificado: false,
  aceiteTermos: false,
  aceiteLgpd: false,
  fuso: "America/Sao_Paulo",
  notifEmail: true,
  notifWhatsapp: false,
  corMarca: "#F4CC04",
  convites: [],
};

export type WsSetField = <K extends keyof WsForm>(k: K, v: WsForm[K]) => void;

// ----------------------------------------------------------- Organização ----

export type OrgContato = {
  kind: string; // "comercial" | "financeiro" | rótulo livre
  fixed?: boolean; // comercial/financeiro não podem ser removidos
  name: string;
  email: string;
  phone: string;
};

export const ORG_CONTATOS_INICIAIS: OrgContato[] = [
  { kind: "comercial", fixed: true, name: "", email: "", phone: "" },
  { kind: "financeiro", fixed: true, name: "", email: "", phone: "" },
];

export type OrgForm = {
  docType: "cnpj" | "cpf";
  nomeOrg: string;
  cnpj: string;
  cpf: string;
  razao: string;
  fantasia: string;
  nomePf: string;
  cidade: string;
  sede: string;
  cnae: string;
  regime: string;
  // Endereço estruturado (editável; pré-preenchido pelo lookup de CNPJ).
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  respCpf: string;
  respUsarWs: boolean;
  aceiteLgpd: boolean;
  certNome: string;
  certFile: File | null;
  certSenha: string;
  certValidade: string;
  certAceiteLgpd: boolean;
  // Contatos da org: começa com comercial + financeiro; "+" adiciona outros.
  contatos: OrgContato[];
  // Aceite: campos exigidos quando email_exists=false (user novo).
  acceptOwnerNome: string;
  acceptPassword: string;
  acceptPassword2: string;
  acceptPhone: string;
  acceptEmail: string;
  acceptEmailVerificado: boolean;
  acceptPhoneVerificado: boolean;
  acceptAceiteTermos: boolean;
  // SEC-030: senha da conta existente (prova de posse) quando email_exists=true.
  existingAccountPassword: string;
};

export type OrgStepId = "org" | "certificado" | "contatos";

export interface OrgStepDef {
  id: OrgStepId;
  label: string;
  short: string;
  required: boolean;
  icon: IconName;
}

export const ORG_STEPS: OrgStepDef[] = [
  {
    id: "org",
    label: "Organização",
    short: "Dados da org",
    required: true,
    icon: "layers",
  },
  {
    id: "certificado",
    label: "Certificado e procurações",
    short: "Acesso ao e-CAC",
    required: false,
    icon: "shield-check",
  },
  {
    id: "contatos",
    label: "Contatos",
    short: "Comercial · financeiro",
    required: false,
    icon: "users",
  },
];

export const ORG_INITIAL_FORM: OrgForm = {
  docType: "cnpj",
  nomeOrg: "",
  cnpj: "",
  cpf: "",
  razao: "",
  fantasia: "",
  nomePf: "",
  cidade: "",
  sede: "SP",
  cnae: "",
  regime: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cep: "",
  respCpf: "",
  respUsarWs: true,
  aceiteLgpd: false,
  certNome: "",
  certFile: null,
  certSenha: "",
  certValidade: "",
  certAceiteLgpd: false,
  contatos: ORG_CONTATOS_INICIAIS.map((c) => ({ ...c })),
  acceptOwnerNome: "",
  acceptPassword: "",
  acceptPassword2: "",
  acceptPhone: "",
  acceptEmail: "",
  acceptEmailVerificado: false,
  acceptPhoneVerificado: false,
  acceptAceiteTermos: false,
  existingAccountPassword: "",
};

export type OrgSetField = <K extends keyof OrgForm>(k: K, v: OrgForm[K]) => void;
